/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorB6,
	ResumeIndicatorB6Data,
} from "@/services/esus/indicator/esb/b6/getResumeIndicatorB6";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorB6ByHealthTeam,
	ResumeIndicatorB6ByHealthTeamData,
} from "@/services/esus/indicator/esb/b6/getResumeIndicatorB6ByHealthTeam";
import {
	getResumeCitizensProceduresIndicatorB6,
	ResumeCitizenProceduresIndicatorB6Data,
} from "@/services/esus/indicator/esb/b6/getResumeCitizensProceduresIndicatorB6";

import { IndicatorEsbB6Resume } from "./IndicatorEsbB6Resume";
import { IndicatorEsbB6HealthTeams } from "./IndicatorEsbB6HealthTeams";
import { IndicatorEsbB6Citizens } from "./IndicatorEsbB6Citizens";

type IndicatorEsbB6Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsbB6({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsbB6Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorB6, setResumeIndicatorB6] = useState<ResumeIndicatorB6Data>(
		new ResumeIndicatorB6Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorB6ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizens, setPageNumberCitizens] = useState(1);
	const [searchCitizens, setSearchCitizens] = useState("");
	const debouncedSearchCitizens = useDebounce(searchCitizens, 2000);
	const [isEvaluatedCitizens, setIsEvaluatedCitizens] = useState<boolean | undefined>(true);
	const [paginatedCitizens, setPaginatedCitizens] = useState(
		new Paginated<ResumeCitizenProceduresIndicatorB6Data>()
	);
	const [isLoadingCitizens, setIsLoadingCitizens] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorB6(),
			handleGetResumeIndicatorB6ByHealthTeam(),
			handleGetResumeCitizensProceduresIndicatorB6(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorB6() {
		setResumeIndicatorB6(
			await getResumeIndicatorB6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorB6ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorB6ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorB6ByHealthTeam() {
		return (
			await getResumeIndicatorB6ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensProceduresIndicatorB6(pageNumber?: number) {
		setIsLoadingCitizens(true);
		setPaginatedCitizens(
			await getResumeCitizensProceduresIndicatorB6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizens(false);
	}

	async function handleGetAllResumeCitizensProceduresIndicatorB6() {
		return (
			await getResumeCitizensProceduresIndicatorB6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: 1,
				pageSize: paginatedCitizens.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorB6ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorB6ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) handleGetResumeCitizensProceduresIndicatorB6(pageNumberCitizens);
	}, [pageNumberCitizens]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizens === 1) {
				handleGetResumeCitizensProceduresIndicatorB6(pageNumberCitizens);
			} else {
				setPageNumberCitizens(1);
			}
		}
	}, [debouncedSearchCitizens, isEvaluatedCitizens]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("B6 - Tratamento Restaurador Atraumático");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsbB6Resume resume={resumeIndicatorB6} />
			<IndicatorEsbB6HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorB6ByHealthTeam={
					handleGetAllResumeIndicatorB6ByHealthTeam
				}
			/>
			<IndicatorEsbB6Citizens
				paginatedCitizens={paginatedCitizens}
				isLoading={isLoadingCitizens}
				search={searchCitizens}
				setSearch={setSearchCitizens}
				isEvaluated={isEvaluatedCitizens}
				setIsEvaluated={setIsEvaluatedCitizens}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberCitizens}
				setPageNumber={setPageNumberCitizens}
				handleGetAllResumeCitizensProceduresIndicatorB6={
					handleGetAllResumeCitizensProceduresIndicatorB6
				}
			/>
		</>
	);
}
